// Enterprise Tour section and step definitions
// Flow: Feed intro -> Buyer agent walkthrough -> Seller agent walkthrough -> Agent collaboration -> Thank you
import { TOUR_SECTIONS } from "./constants";

const tourSections = [
  // ====================================================
  // Section 0: The Feed - introduce buyer demand (3 steps)
  // ====================================================
  {
    id: "the-feed",
    index: TOUR_SECTIONS.THE_FEED,
    title: "The Feed",
    page: "/home",
    cutScene: null,
    steps: [
      {
        target: "[data-tour='feed']",
        title: "Welcome to Real-Time Buyer Demand",
        description:
          "This is the home feed - where you can view live buyer demand like never before. The MLS has always listed for-sale inventory. Now, we're making the other side visible: what buyers are actively looking for.",
        placement: "right",
        noScroll: true,
      },
      {
        target: "[data-tour='feed-main']",
        title: "Buyer Want-Listings",
        description:
          "These are Buyer Want-Listings - just like for-sale listings, except they represent buyer demand. Each one is posted by a buyer's agent on behalf of their client, the same way selling agents list properties for sale.",
        placement: "left",
        noScroll: true,
      },
      {
        target: ".home-listing-wrapper .property-listing-link:first-child",
        title: "Organized. Searchable. Matchable.",
        description:
          "For the first time, buyer demand is captured in a structured, searchable system. This gives buyer agents the ability to be proactive and transparent. It gives selling agents real-time insight into top market demand. And at the MLS level, it gives you exclusive access to live buyer data.",
        placement: "right",
        noScroll: true,
      },
    ],
  },

  // ====================================================
  // Section 1: Cut scene - The Buyer Agent
  // ====================================================
  {
    id: "buyer-agent-cutscene",
    index: TOUR_SECTIONS.BUYER_AGENT_CUTSCENE,
    title: "Buyer Agent",
    page: null,
    cutScene: {
      variant: "standard",
      headline: "How Buyer Agents Use\nThe Buyer Listing Service\u00AE",
      subline: "The first protocol for listing buyer demand.",
      duration: 3000,
    },
    steps: [],
  },

  // ====================================================
  // Section 2: My Want-Listings Nav (1 step)
  // ====================================================
  {
    id: "my-want-listings-nav",
    index: TOUR_SECTIONS.MY_WANT_LISTINGS_NAV,
    title: "Want-Listings",
    page: "/home",
    cutScene: null,
    steps: [
      {
        target: "[data-tour='want-listings']",
        title: "My Want-Listings",
        description:
          "Let's navigate to your Want-Listings.",
        placement: "right",
        noScroll: true,
      },
    ],
  },

  // ====================================================
  // Section 3: My Want-Listings Dashboard (2 steps)
  // ====================================================
  {
    id: "my-want-listings-dashboard",
    index: TOUR_SECTIONS.MY_WANT_LISTINGS_DASHBOARD,
    title: "Dashboard",
    page: "/my-want-listings",
    cutScene: null,
    steps: [
      {
        target: ".dashboard-table-wrap",
        title: "Your Want-Listing Dashboard",
        description:
          "We've created the first protocol for buyer agents to input and broadcast buyer demand. Every Want-Listing you post lives here - structured, searchable, and matched automatically.",
        placement: "bottom",
        noScroll: true,
        waitFor: "tbody tr:first-child",
      },
      {
        target: "tbody tr:first-child",
        title: "Dive Into a Want-Listing",
        description:
          "Let's click into one to see the full detail.",
        placement: "bottom",
        noScroll: true,
        nextAction: "clickMyWantListing",
      },
    ],
  },

  // ====================================================
  // Section 4: Want-Listing Walkthrough (7 steps)
  // ====================================================
  {
    id: "want-listing-walkthrough",
    index: TOUR_SECTIONS.WANT_LISTING_WALKTHROUGH,
    title: "Walkthrough",
    page: "/want-listing-details/{myWantAdSlug}",
    setupLocalStorage: {
      key: "memberBuy",
      value: { introId: 1 },
    },
    cutScene: null,
    steps: [
      {
        target: "[data-tour='wantad-title']",
        title: "Title Your Buyer's Search",
        description:
          "Buyer agents give each Want-Listing a clear, descriptive title - making it easy for selling agents to quickly understand what the buyer is looking for.",
        placement: "right",
        noScroll: true,
      },
      {
        target: "[data-tour='wantad-location']",
        title: "Pinpoint the Location",
        description:
          "Specify desired radius, ZIP code(s), or draw a border.",
        placement: "right",
        noScroll: true,
      },
      {
        target: "[data-tour='wantad-musthaves']",
        title: "Must-Haves and Nice-to-Haves",
        description:
          "Agents list their buyer's must-haves - bedrooms, bathrooms, square footage, budget - as well as nice-to-haves they can select from a curated set of features that would be a plus for their client.",
        placement: "right",
        scrollBehavior: "smooth",
      },
      {
        target: "[data-tour='wantad-description']",
        title: "The Full Story",
        description:
          "This is where buyer agents provide all the key context about what their buyer is really looking for - beyond the limitations of checkboxes and dropdowns. This language gives selling agents significantly more context, and gives you at the MLS level a richer understanding of actual buyer intent.",
        placement: "right",
        scrollBehavior: "smooth",
      },
      {
        target: "[data-tour='buyer-credibility']",
        title: "The Details Matter",
        description:
          "Buyer agents can indicate whether they have a signed buyer agreement, their timeline, preferred neighborhoods and school districts, financing type - cash, pre-approved, FHA - and whether this is a primary residence or investment property. This context helps selling agents prioritize serious, qualified buyers.",
        placement: "right",
        scrollBehavior: "smooth",
      },
    ],
  },

  // ====================================================
  // Section 5: Want-Listing Matches (3 steps)
  // ====================================================
  {
    id: "want-listing-matches",
    index: TOUR_SECTIONS.WANT_LISTING_MATCHES,
    title: "Matches",
    page: "/want-listing-details/{myWantAdSlug}",
    setupLocalStorage: { key: "memberBuy", value: { introId: 1 } },
    cutScene: null,
    steps: [
      {
        target: ".tab-wrap .nav-item:last-child .nav-link",
        title: "Beyond Broadcasting",
        description:
          "Broadcasting buyer demand is powerful on its own - agents already do this in Facebook groups, text threads, email chains, and office meetings. But what if we could take it a step further?",
        placement: "bottom",
        scrollBehavior: "smooth-to-top",
        nextAction: "clickMatchesTab",
      },
      {
        target: "[data-tour='matches-results']",
        title: "Automatic Matching to Active Listings",
        description:
          "Every Want-Listing is automatically matched against active and coming-soon listings in your MLS. Instead of manually searching, buyer agents see relevant properties surfaced for them - saving time and ensuring nothing gets missed.",
        placement: "right",
        noScroll: true,
        waitFor: "[data-tour='matches-results']",
      },
      {
        target: "[data-tour='match-score-badge']",
        title: "Match Percentages",
        description:
          "Each match includes a percentage score so buyer agents can quickly prioritize which listings to show their client first. Higher scores mean a closer fit based on the buyer's full criteria - budget, location, property type, size, and features.",
        placement: "left",
        noScroll: true,
      },
      {
        target: "[data-tour='matches-results']",
        title: "Property Matches Redirect to the MLS",
        description:
          "The Buyer Listing Service\u00AE does not host properties actively listed for sale. All matches shown here are sourced directly from your MLS. Clicking any listing redirects the agent to view full details on the MLS - photos, property history, tax records, and more.",
        placement: "top",
        noScroll: true,
      },
    ],
  },

  // ====================================================
  // Section 6: Cut scene - The Seller Agent
  // ====================================================
  {
    id: "seller-agent-cutscene",
    index: TOUR_SECTIONS.SELLER_AGENT_CUTSCENE,
    title: "Seller Agent",
    page: null,
    cutScene: {
      variant: "map",
      headline: "How Selling Agents Use\nThe Buyer Listing Service\u00AE",
      subline: "Real-time demand data for every property.",
      duration: 3000,
    },
    steps: [],
  },

  // ====================================================
  // Section 7: My Inventory Nav (1 step)
  // ====================================================
  {
    id: "my-inventory-nav",
    index: TOUR_SECTIONS.MY_INVENTORY_NAV,
    title: "Inventory",
    page: "/want-listing-details/{myWantAdSlug}",
    setupLocalStorage: {
      key: "memberBuy",
      value: { introId: 1 },
    },
    cutScene: null,
    steps: [
      {
        target: "[data-tour='inventory']",
        title: "The Selling Agent's Perspective",
        description:
          "As you can imagine, this system is just as valuable for selling agents. Let's walk through how a selling agent uses The Buyer Listing Service\u00AE.",
        placement: "right",
        noScroll: true,
      },
    ],
  },

  // ====================================================
  // Section 8: My Inventory Dashboard (2 steps)
  // ====================================================
  {
    id: "my-inventory-dashboard",
    index: TOUR_SECTIONS.MY_INVENTORY_DASHBOARD,
    title: "Demand Data",
    page: "/my-inventory",
    cutScene: null,
    steps: [
      {
        target: ".dashboard-table-wrap",
        title: "Your Inventory Dashboard",
        description:
          "This is your inventory dashboard. Every property you're working with lives here - prospects, active listings, and coming-soon properties. Each one shows how many active buyers match it in real-time.",
        placement: "bottom",
        noScroll: true,
      },
      {
        target: "tbody",
        title: "Beyond Reverse Prospecting",
        description:
          'Unlike reverse prospecting, you don\'t have to wait until a property is listed on the MLS. Load a prospect\'s address, gauge <span style="color: #4ade80; font-weight: 600;">live buyer demand</span>, and download a report - all before your listing appointment. This gives you a data-driven edge in every pricing and go-to-market conversation.',
        allowHtml: true,
        placement: "left",
        noScroll: true,
        nextAction: "clickInventoryProperty",
      },
    ],
  },

  // ====================================================
  // Section 9: Inventory Matches - Feed Match Tab (5 steps)
  // ====================================================
  {
    id: "inventory-matches",
    index: TOUR_SECTIONS.INVENTORY_MATCHES,
    title: "Inventory Matches",
    page: "/home",
    setupLocalStorage: {
      key: "FilterTabKey",
      value: "match",
    },
    cutScene: null,
    steps: [
      {
        target: "#controlled-tab-example-tab-match",
        title: "Sit Back and Get Alerts",
        description:
          "Get alerts when buyer want-listings match properties in your inventory. The Match tab shows every Want-Listing that aligns with your client's property - with AI-powered match scores, budget ranges, and buyer criteria.",
        placement: "bottom",
        scrollBehavior: "smooth-to-top",
        nextAction: "clickMatchTabOnFeed",
      },
      {
        target: "[data-tour='demand-panel']",
        title: "Demand Insights",
        description:
          "Active buyer count, peak demand price, price distribution, and an interactive slider - all powered by live Want-Listing data. This is the demand side of the market made visible, giving selling agents real data for listing appointments, pricing discussions, and go-to-market strategy.",
        placement: "right",
        scrollBehavior: "smooth",
        waitFor: "[data-tour='demand-panel']",
      },
    ],
  },

  // ====================================================
  // Section 10: Reporting (1 step) - Download Report on Match panel
  // ====================================================
  {
    id: "reporting",
    index: TOUR_SECTIONS.REPORTING,
    title: "Reporting",
    page: "/home",
    setupLocalStorage: {
      key: "FilterTabKey",
      value: "match",
    },
    cutScene: null,
    steps: [
      {
        target: "[data-tour='download-report-match']",
        title: "Downloadable Reports",
        description:
          "Agents can download buyer demand reports to bring to their listing appointments and pricing discussions. This isn't meant to replace CMAs - it's a complement. Real buyer data that adds context to market analysis.",
        placement: "top",
        scrollBehavior: "smooth",
        waitFor: "[data-tour='download-report-match']",
      },
    ],
  },

  // ====================================================
  // Section 11: Cut scene - Agent Collaboration
  // ====================================================
  {
    id: "collaboration-cutscene",
    index: TOUR_SECTIONS.COLLABORATION_CUTSCENE,
    title: "Collaboration",
    page: null,
    cutScene: {
      variant: "data",
      headline: "Collaboration Between Agents",
      subline: "Compliant. Efficient. Transparent.",
      duration: 3000,
    },
    steps: [],
  },

  // ====================================================
  // Section 12: Browse Feed (1 step)
  // ====================================================
  {
    id: "browse-feed",
    index: TOUR_SECTIONS.BROWSE_FEED,
    title: "Browse",
    page: "/home",
    cutScene: null,
    steps: [
      {
        // Phase 3: Seller finds a Want-Listing that fits their property
        title: "Found a Want-Listing That Fits Your Property?",
        allowHtml: true,
        centered: true,
        description: `
          <div class="et-phase-graphic">
            <svg viewBox="0 0 360 120" fill="none" xmlns="http://www.w3.org/2000/svg" class="et-phase-graphic__svg">
              <rect x="8" y="16" width="100" height="88" rx="10" fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.15)" stroke-width="1.5"/>
              <rect x="20" y="28" width="76" height="36" rx="4" fill="rgba(255,255,255,0.08)"/>
              <rect x="26" y="34" width="24" height="24" rx="3" fill="rgba(0,126,245,0.2)" stroke="rgba(0,126,245,0.4)" stroke-width="1"/>
              <path d="M34 50 L38 46 L42 50 L38 42Z" fill="rgba(0,126,245,0.5)"/>
              <rect x="56" y="36" width="34" height="4" rx="2" fill="rgba(255,255,255,0.25)"/>
              <rect x="56" y="44" width="24" height="3" rx="1.5" fill="rgba(255,255,255,0.12)"/>
              <rect x="56" y="51" width="28" height="3" rx="1.5" fill="rgba(255,255,255,0.12)"/>
              <text x="22" y="84" fill="rgba(255,255,255,0.5)" font-size="9" font-weight="500" font-family="system-ui, sans-serif">Your Property</text>
              <line x1="124" y1="60" x2="170" y2="60" stroke="rgba(0,126,245,0.5)" stroke-width="1.5" stroke-dasharray="4 3"/>
              <polygon points="172,55 182,60 172,65" fill="rgba(0,126,245,0.6)"/>
              <rect x="192" y="16" width="160" height="88" rx="10" fill="rgba(0,126,245,0.08)" stroke="rgba(0,126,245,0.3)" stroke-width="1.5"/>
              <rect x="204" y="28" width="136" height="10" rx="3" fill="rgba(0,126,245,0.15)"/>
              <text x="215" y="36" fill="rgba(0,126,245,0.7)" font-size="7" font-weight="600" font-family="system-ui, sans-serif">WANT-LISTING</text>
              <rect x="204" y="44" width="80" height="4" rx="2" fill="rgba(255,255,255,0.2)"/>
              <rect x="204" y="52" width="60" height="3" rx="1.5" fill="rgba(255,255,255,0.1)"/>
              <rect x="204" y="59" width="70" height="3" rx="1.5" fill="rgba(255,255,255,0.1)"/>
              <rect x="204" y="66" width="50" height="3" rx="1.5" fill="rgba(255,255,255,0.1)"/>
              <circle cx="332" cy="36" r="14" fill="rgba(34,197,94,0.15)" stroke="rgba(34,197,94,0.5)" stroke-width="1.5"/>
              <path d="M326 36 L330 40 L338 32" stroke="rgba(34,197,94,0.8)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
              <text x="228" y="86" fill="rgba(255,255,255,0.5)" font-size="9" font-weight="500" font-family="system-ui, sans-serif">Potential Match</text>
            </svg>
          </div>
          <span class="et-phase-graphic__text">You're browsing the feed as a selling agent and spot a Want-Listing that could be a fit for one of your properties. Here's what happens next.</span>
        `,
        noScroll: true,
      },
    ],
  },

  // ====================================================
  // Section 13: Agent Communication (3 steps)
  // ====================================================
  {
    id: "agent-communication",
    index: TOUR_SECTIONS.AGENT_COMMUNICATION,
    title: "Communication",
    page: "/want-listing-details/{otherWantAdSlug}",
    setupLocalStorage: {
      key: "memberBuy",
      value: { introId: 1 },
    },
    cutScene: null,
    steps: [
      {
        target: "[data-tour='message-area']",
        title: "Communicate with Guardrails",
        description:
          "If you have questions or need more context, you can reach out to the buyer's agent directly through the messaging portal. For example - if a buyer wants a home with a pool, but you have a property that's perfect in every other way with space to add one at a different price point, you can ask if their client is open to that.",
        placement: "left",
        noScroll: true,
      },
      {
        target: "[data-tour='clear-cooperation']",
        title: "Blacklisted Words and CCP Acknowledgement",
        description:
          "Agents must agree to adhere to the Clear Cooperation Policy before sending any message. We also blacklist certain words and phrases - like specific addresses, 'off-market,' 'pocket listing,' and others - to put guardrails in place for compliance. This keeps communication professional and within MLS standards.",
        placement: "left",
        scrollBehavior: "smooth",
      },
      {
        target: "[data-tour='send-intro-panel']",
        title: "Already Have an Active Listing in the MLS That Fits?",
        description:
          "Send your listing directly in a couple clicks. Agents can only select from their active or coming-soon listings already in the MLS - private inventory stored outside the MLS cannot be sent here. This ensures speed and compliance.",
        placement: "left",
        scrollBehavior: "smooth",
        waitFor: ".make-intro-img, #propertySelection",
      },
    ],
  },

  // ====================================================
  // Section 14: Thank You - Final cut scene
  // ====================================================
  {
    id: "thank-you",
    index: TOUR_SECTIONS.THANK_YOU,
    title: "Thank You",
    page: "/home",
    cutScene: {
      variant: "data",
      headline: "Thanks for Taking the Tour",
      subline: "Contact us at sales@gitcha.com with any questions or to schedule a more in-depth walk-through.",
      duration: 4000,
      isFinal: true,
    },
    steps: [],
  },
];

export default tourSections;
